import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import { getActiveShopId } from 'src/utils/shopContext';

const getIdTokenOrThrow = async () => {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error('Authentication is still loading. Please retry.');
  }

  return user.getIdToken();
};

const authConfig = async () => {
  const idToken = await getIdTokenOrThrow();
  return {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  };
};

export const requestPhoneSettings = createAsyncThunk('phone/requestPhoneSettings', async () => {
  const response = await axios.get(buildApiUrl('/api/phone/settings'), await authConfig());
  return response.data;
});

export const updatePhoneSettings = createAsyncThunk('phone/updatePhoneSettings', async (payload, { rejectWithValue }) => {
  try {
    const response = await axios.put(buildApiUrl('/api/phone/settings'), payload, await authConfig());
    return response.data;
  } catch (error) {
    return rejectWithValue(error?.response?.data?.error || error.message || 'Unable to save phone settings.');
  }
});

export const requestPhoneNumber = createAsyncThunk('phone/requestPhoneNumber', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/phone/settings/number-request'), payload, await authConfig());
  return response.data;
});

export const requestPhonePortRequests = createAsyncThunk('phone/requestPhonePortRequests', async () => {
  const response = await axios.get(buildApiUrl('/api/phone/port-requests'), await authConfig());
  return response.data;
});

export const checkPhonePortability = createAsyncThunk('phone/checkPhonePortability', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/phone/port-requests/check-portability'), payload, await authConfig());
  return response.data;
});

export const createPhonePortRequest = createAsyncThunk('phone/createPhonePortRequest', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/phone/port-requests'), payload, await authConfig());
  return response.data;
});

export const submitPhonePortRequest = createAsyncThunk('phone/submitPhonePortRequest', async (requestId) => {
  const response = await axios.post(buildApiUrl(`/api/phone/port-requests/${requestId}/submit`), {}, await authConfig());
  return response.data;
});

export const refreshPhonePortRequest = createAsyncThunk('phone/refreshPhonePortRequest', async (requestId) => {
  const response = await axios.post(buildApiUrl(`/api/phone/port-requests/${requestId}/refresh`), {}, await authConfig());
  return response.data;
});

export const createPhoneRoute = createAsyncThunk('phone/createPhoneRoute', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/phone/routes'), payload, await authConfig());
  return response.data;
});

export const updatePhoneRoute = createAsyncThunk('phone/updatePhoneRoute', async ({ routeId, ...payload }) => {
  const response = await axios.put(buildApiUrl(`/api/phone/routes/${routeId}`), payload, await authConfig());
  return response.data;
});

export const deletePhoneRoute = createAsyncThunk('phone/deletePhoneRoute', async (routeId) => {
  const response = await axios.delete(buildApiUrl(`/api/phone/routes/${routeId}`), await authConfig());
  return response.data;
});

export const addPhoneRouteTarget = createAsyncThunk('phone/addPhoneRouteTarget', async ({ routeId, ...payload }) => {
  const response = await axios.post(buildApiUrl(`/api/phone/routes/${routeId}/targets`), payload, await authConfig());
  return response.data;
});

export const deletePhoneRouteTarget = createAsyncThunk('phone/deletePhoneRouteTarget', async (targetId) => {
  const response = await axios.delete(buildApiUrl(`/api/phone/route-targets/${targetId}`), await authConfig());
  return response.data;
});

const phoneMappingScope = () => {
  const uid = firebase.auth().currentUser?.uid;
  return uid ? `${uid}:${getActiveShopId() || ''}` : null;
};
const updateStaffMapping = (role) => createAsyncThunk(
  `phone/updatePhone${role === 'advisor' ? 'Advisor' : 'Technician'}Mapping`,
  async ({ advisorId, technicianId, shoplineEmployeeId }, { signal, rejectWithValue }) => {
    const scope = phoneMappingScope();
    const shopId = getActiveShopId();
    try {
      const config = await authConfig();
      if (!scope || signal.aborted || phoneMappingScope() !== scope) throw new Error('Account changed. Refresh phone settings.');
      const id = role === 'advisor' ? advisorId : technicianId;
      const response = await axios.put(buildApiUrl(`/api/phone/${role === 'advisor' ? 'advisors' : 'technicians'}/${id}/mapping`), {
        shoplineEmployeeId
      }, { ...config, signal, timeout: 15000, headers: { ...config.headers, 'X-Shop-Id': String(shopId) } });
      if (signal.aborted || phoneMappingScope() !== scope) throw new Error('Account changed. Refresh phone settings.');
      return response.data;
    } catch (error) {
      return rejectWithValue({ scope, message: error?.response?.data?.error || error.message || 'Unable to update staff mapping.' });
    }
  }, {
    getPendingMeta: () => ({ scope: phoneMappingScope() }),
    condition: (_, { getState }) => !getState().phone?.mappingLoading
  }
);
export const updatePhoneAdvisorMapping = updateStaffMapping('advisor');
export const updatePhoneTechnicianMapping = updateStaffMapping('technician');

export const requestPhoneCustomers = createAsyncThunk('phone/requestPhoneCustomers', async (search = '') => {
  const response = await axios.get(buildApiUrl('/api/phone/customers/search'), {
    ...(await authConfig()),
    params: { search }
  });
  return response.data;
});

export const requestPhoneCalls = createAsyncThunk('phone/requestPhoneCalls', async (params = {}) => {
  const response = await axios.get(buildApiUrl('/api/phone/calls'), {
    ...(await authConfig()),
    params
  });
  return { ...response.data, request: params };
});

export const requestPhoneCenter = createAsyncThunk('phone/requestPhoneCenter', async () => {
  const response = await axios.get(buildApiUrl('/api/phone/command-center'), await authConfig());
  return response.data;
});

export const updatePhoneEmployeeQueue = createAsyncThunk(
  'phone/updatePhoneEmployeeQueue',
  async ({ employeeId, queueEnabled }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        buildApiUrl(`/api/phone/employees/${employeeId}/queue`),
        { queueEnabled },
        await authConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error.message || 'Unable to change call availability.');
    }
  }
);

export const requestPhoneCallDetail = createAsyncThunk('phone/requestPhoneCallDetail', async (callSid) => {
  const response = await axios.get(buildApiUrl(`/api/phone/calls/${callSid}`), await authConfig());
  return response.data;
});

export const startPhoneCall = createAsyncThunk(
  'phone/startPhoneCall',
  async ({ customerId, serviceAdvisorId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(buildApiUrl(`/api/phone/customers/${customerId}/call`), {
        serviceAdvisorId
      }, await authConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error.message || 'Unable to start outbound call.');
    }
  }
);

const initialState = {
  settingsLoading: false,
  settingsError: null,
  shop: null,
  integration: null,
  remoteShop: null,
  routes: [],
  availableEmployees: [],
  serviceAdvisors: [],
  technicians: [],
  staffSyncError: null,
  mappingLoading: false,
  mappingRequestId: null,
  mappingScope: null,
  activeServiceAdvisorId: null,
  customerSearchTerm: '',
  customerSearchLoading: false,
  customerSearchResults: [],
  selectedCustomerId: null,
  callsLoading: false,
  callsError: null,
  calls: [],
  commandCenterLoading: false,
  commandCenterError: null,
  commandCenterConnected: false,
  commandCenterEmployees: [],
  activeCalls: [],
  commandCenterGeneratedAt: null,
  callsPagination: {
    page: 1,
    pageSize: 20,
    total: 0
  },
  selectedCallSid: null,
  callDetailLoading: false,
  callDetail: null,
  outboundCalling: false,
  outboundCallResult: null,
  outboundCallError: null,
  portRequests: [],
  portRequestsLoading: false,
  portabilityCheck: null,
  portabilityChecking: false,
  portRequestSubmitting: false,
  actionMessage: null
};

const hydrateSettingsState = (state, payload) => {
  state.shop = payload.shop;
  state.integration = payload.integration;
  state.remoteShop = payload.remote_shop || null;
  state.routes = payload.routes || [];
  state.availableEmployees = payload.available_employees || [];
  state.serviceAdvisors = payload.service_advisors || [];
  state.technicians = payload.technicians || [];
  state.staffSyncError = typeof payload.staff_sync_error === 'string' ? payload.staff_sync_error : null;
};

const slice = createSlice({
  name: 'phone',
  initialState,
  reducers: {
    setActiveServiceAdvisorId(state, action) {
      state.activeServiceAdvisorId = action.payload || null;
    },
    setSelectedCustomerId(state, action) {
      state.selectedCustomerId = action.payload || null;
    },
    setSelectedCallSid(state, action) {
      state.selectedCallSid = action.payload || null;
    },
    setCustomerSearchTerm(state, action) {
      state.customerSearchTerm = action.payload || '';
    },
    clearPhoneActionMessage(state) {
      state.actionMessage = null;
      state.outboundCallError = null;
    },
    receivePhoneCenterSnapshot(state, action) {
      const payload = action.payload || {};
      state.commandCenterEmployees = payload.employees || [];
      state.activeCalls = payload.active_calls || [];
      state.commandCenterGeneratedAt = payload.generated_at || new Date().toISOString();
      state.commandCenterError = null;
      state.commandCenterLoading = false;
    },
    setPhoneCenterConnected(state, action) {
      state.commandCenterConnected = Boolean(action.payload);
    },
    setPhoneCenterError(state, action) {
      state.commandCenterError = action.payload || null;
    }
  },
  extraReducers: (builder) => {
    [updatePhoneAdvisorMapping, updatePhoneTechnicianMapping].forEach((request) => {
      builder.addCase(request.pending, (state, action) => {
        state.mappingRequestId = action.meta.requestId;
        state.mappingScope = action.meta.scope;
        state.mappingLoading = true;
        state.settingsError = null;
      }).addCase(request.fulfilled, (state, action) => {
        if (state.mappingRequestId !== action.meta.requestId) return;
        state.mappingLoading = false;
        state.mappingRequestId = null;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Staff phone connection updated.';
      }).addCase(request.rejected, (state, action) => {
        if (state.mappingRequestId !== action.meta.requestId) return;
        state.mappingLoading = false;
        state.mappingRequestId = null;
        if (action.payload?.scope === phoneMappingScope()) state.settingsError = action.payload?.message || 'Unable to update staff mapping.';
      });
    });
    builder
      .addCase(requestPhoneSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(requestPhoneSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
      })
      .addCase(requestPhoneSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to load phone settings.';
      })
      .addCase(updatePhoneSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(updatePhoneSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Phone system settings saved.';
      })
      .addCase(updatePhoneSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload || action.error.message || 'Unable to save phone settings.';
      })
      .addCase(requestPhoneNumber.pending, (state) => {
        state.settingsLoading = true;
        state.actionMessage = null;
      })
      .addCase(requestPhoneNumber.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.integration = action.payload.integration;
        state.actionMessage = action.payload.message || 'Phone number request saved.';
      })
      .addCase(requestPhoneNumber.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to request phone number.';
      })
      .addCase(requestPhonePortRequests.pending, (state) => {
        state.portRequestsLoading = true;
      })
      .addCase(requestPhonePortRequests.fulfilled, (state, action) => {
        state.portRequestsLoading = false;
        state.portRequests = action.payload.items || [];
      })
      .addCase(requestPhonePortRequests.rejected, (state, action) => {
        state.portRequestsLoading = false;
        state.settingsError = action.error.message || 'Unable to load port requests.';
      })
      .addCase(checkPhonePortability.pending, (state) => {
        state.portabilityChecking = true;
        state.portabilityCheck = null;
        state.actionMessage = null;
      })
      .addCase(checkPhonePortability.fulfilled, (state, action) => {
        state.portabilityChecking = false;
        state.portabilityCheck = action.payload;
        state.actionMessage = action.payload.can_submit_automatically
          ? 'Number is portable through Twilio automation.'
          : 'Portability checked. Manual follow-up may be required.';
      })
      .addCase(checkPhonePortability.rejected, (state, action) => {
        state.portabilityChecking = false;
        state.settingsError = action.error.message || 'Unable to check portability.';
      })
      .addCase(createPhonePortRequest.pending, (state) => {
        state.portRequestSubmitting = true;
        state.actionMessage = null;
      })
      .addCase(createPhonePortRequest.fulfilled, (state, action) => {
        state.portRequestSubmitting = false;
        state.integration = action.payload.integration || state.integration;
        const request = action.payload.port_request;
        if (request) {
          state.portRequests = [
            request,
            ...state.portRequests.filter((item) => Number(item.id) !== Number(request.id))
          ];
        }
        state.actionMessage = action.payload.message || 'Port request saved.';
      })
      .addCase(createPhonePortRequest.rejected, (state, action) => {
        state.portRequestSubmitting = false;
        state.settingsError = action.error.message || 'Unable to create port request.';
      })
      .addCase(submitPhonePortRequest.pending, (state) => {
        state.portRequestSubmitting = true;
        state.actionMessage = null;
      })
      .addCase(submitPhonePortRequest.fulfilled, (state, action) => {
        state.portRequestSubmitting = false;
        const request = action.payload.port_request;
        if (request) {
          state.portRequests = state.portRequests.map((item) => (
            Number(item.id) === Number(request.id) ? request : item
          ));
        }
        state.actionMessage = action.payload.message || 'Port request submitted.';
      })
      .addCase(submitPhonePortRequest.rejected, (state, action) => {
        state.portRequestSubmitting = false;
        state.settingsError = action.error.message || 'Unable to submit port request.';
      })
      .addCase(refreshPhonePortRequest.fulfilled, (state, action) => {
        const request = action.payload.port_request;
        if (request) {
          state.portRequests = state.portRequests.map((item) => (
            Number(item.id) === Number(request.id) ? request : item
          ));
        }
        state.actionMessage = action.payload.message || 'Port request refreshed.';
      })
      .addCase(createPhoneRoute.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(createPhoneRoute.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Phone route created.';
      })
      .addCase(createPhoneRoute.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to create phone route.';
      })
      .addCase(updatePhoneRoute.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(updatePhoneRoute.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Phone route updated.';
      })
      .addCase(updatePhoneRoute.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to update phone route.';
      })
      .addCase(deletePhoneRoute.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(deletePhoneRoute.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Phone route deleted.';
      })
      .addCase(deletePhoneRoute.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to delete phone route.';
      })
      .addCase(addPhoneRouteTarget.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(addPhoneRouteTarget.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Route target added.';
      })
      .addCase(addPhoneRouteTarget.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to add route target.';
      })
      .addCase(deletePhoneRouteTarget.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
        state.actionMessage = null;
      })
      .addCase(deletePhoneRouteTarget.fulfilled, (state, action) => {
        state.settingsLoading = false;
        hydrateSettingsState(state, action.payload);
        state.actionMessage = 'Route target deleted.';
      })
      .addCase(deletePhoneRouteTarget.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message || 'Unable to delete route target.';
      })
      .addCase(requestPhoneCustomers.pending, (state) => {
        state.customerSearchLoading = true;
      })
      .addCase(requestPhoneCustomers.fulfilled, (state, action) => {
        state.customerSearchLoading = false;
        state.customerSearchResults = action.payload.items || [];
      })
      .addCase(requestPhoneCustomers.rejected, (state) => {
        state.customerSearchLoading = false;
      })
      .addCase(requestPhoneCalls.pending, (state) => {
        state.callsLoading = true;
        state.callsError = null;
      })
      .addCase(requestPhoneCalls.fulfilled, (state, action) => {
        state.callsLoading = false;
        state.calls = action.payload.items || [];
        state.callsPagination = {
          page: action.payload.page || action.payload.request?.page || 1,
          pageSize: action.payload.pageSize || action.payload.request?.pageSize || 20,
          total: action.payload.total || 0
        };
        const requestCustomerId = Number(action.payload.request?.customerId || 0) || null;
        if (requestCustomerId) {
          state.selectedCustomerId = requestCustomerId;
        }
        if (!state.selectedCallSid && state.calls.length > 0) {
          state.selectedCallSid = state.calls[0].call_sid;
        }
      })
      .addCase(requestPhoneCalls.rejected, (state, action) => {
        state.callsLoading = false;
        state.callsError = action.error.message || 'Unable to load calls.';
      })
      .addCase(requestPhoneCenter.pending, (state) => {
        state.commandCenterLoading = true;
        state.commandCenterError = null;
      })
      .addCase(requestPhoneCenter.fulfilled, (state, action) => {
        state.commandCenterLoading = false;
        state.commandCenterEmployees = action.payload.employees || [];
        state.activeCalls = action.payload.active_calls || [];
        state.commandCenterGeneratedAt = action.payload.generated_at || new Date().toISOString();
      })
      .addCase(requestPhoneCenter.rejected, (state, action) => {
        state.commandCenterLoading = false;
        state.commandCenterError = action.error.message || 'Unable to load the phone command center.';
      })
      .addCase(updatePhoneEmployeeQueue.pending, (state, action) => {
        const employeeId = String(action.meta.arg.employeeId || '');
        state.commandCenterError = null;
        state.commandCenterEmployees = state.commandCenterEmployees.map((employee) => (
          String(employee.id) === employeeId
            ? {
              ...employee,
              queue_enabled: action.meta.arg.queueEnabled,
              previous_queue_enabled: employee.queue_enabled
            }
            : employee
        ));
      })
      .addCase(updatePhoneEmployeeQueue.fulfilled, (state, action) => {
        state.commandCenterEmployees = action.payload.employees || [];
        state.activeCalls = action.payload.active_calls || [];
        state.commandCenterGeneratedAt = action.payload.generated_at || new Date().toISOString();
      })
      .addCase(updatePhoneEmployeeQueue.rejected, (state, action) => {
        const employeeId = String(action.meta.arg.employeeId || '');
        state.commandCenterEmployees = state.commandCenterEmployees.map((employee) => (
          String(employee.id) === employeeId
            ? {
              ...employee,
              queue_enabled: employee.previous_queue_enabled ?? !action.meta.arg.queueEnabled,
              previous_queue_enabled: undefined
            }
            : employee
        ));
        state.commandCenterError = action.payload || action.error.message || 'Unable to change call availability.';
      })
      .addCase(requestPhoneCallDetail.pending, (state) => {
        state.callDetailLoading = true;
      })
      .addCase(requestPhoneCallDetail.fulfilled, (state, action) => {
        state.callDetailLoading = false;
        state.callDetail = action.payload;
      })
      .addCase(requestPhoneCallDetail.rejected, (state, action) => {
        state.callDetailLoading = false;
        state.callsError = action.error.message || 'Unable to load call detail.';
      })
      .addCase(startPhoneCall.pending, (state) => {
        state.outboundCalling = true;
        state.outboundCallError = null;
        state.actionMessage = null;
      })
      .addCase(startPhoneCall.fulfilled, (state, action) => {
        state.outboundCalling = false;
        state.outboundCallResult = action.payload;
        state.actionMessage = 'Outbound call started.';
      })
      .addCase(startPhoneCall.rejected, (state, action) => {
        state.outboundCalling = false;
        state.outboundCallError = action.payload || action.error.message || 'Unable to start outbound call.';
      });
  }
});

export const {
  setActiveServiceAdvisorId,
  setSelectedCustomerId,
  setSelectedCallSid,
  setCustomerSearchTerm,
  clearPhoneActionMessage,
  receivePhoneCenterSnapshot,
  setPhoneCenterConnected,
  setPhoneCenterError
} = slice.actions;

export const reducer = slice.reducer;
